import {Generics} from "./generics.ts";
import {Artist} from "../models/db/tri/Artist.ts";
import {compute, Signal, signal} from "../../fjsc/src/signals.ts";
import {Api} from "../api/api.ts";
import {create, ifjs, signalMap, StringOrSignal} from "../../fjsc/src/f2.ts";
import {RequestableImageSize} from "./requestableImageSize.ts";
import {MediaFileType} from "../enums/MediaFileType.ts";
import {getImageUrl} from "../functions/templates.ts";
import {Route} from "../routing/Route.ts";
import {navigate} from "../routing/Router.ts";
import {Time} from "../functions/Time.ts";
import {Tracks} from "./tracks.ts";
import {Albums} from "./albums.ts";

const artists = signal<Artist[]>([]);
const sortedArtists = compute(a => a.sort((a, b) => a.has_logo === b.has_logo ? 0 : a.has_logo ? -1 : 1), artists);

export class Artists {
    static page() {
        const loading = signal(false);
        loading.value = true;
        Api.getArtists()
            .then(a => artists.value = a)
            .finally(() => loading.value = false);

        return Generics.pageFrame(
            Generics.heading(1, "Roster", ["bold"]),
            ifjs(loading, Generics.loading()),
            signalMap(sortedArtists, create("div").classes("flex"), a => create("button")
                .classes("fjsc", "center-items")
                .onclick(() => navigate("/artist/" + a.name))
                .children(
                    ifjs(a.has_logo, Generics.image(getImageUrl(MediaFileType.artistLogo, a.id, RequestableImageSize.s50), ["artist-icon"])),
                    Generics.heading(2, a.name)
                ).build())
        );
    }

    static artistPage(route: Route, params: any) {
        let name = params.name;
        if (!name) {
            const urlParams = new URLSearchParams(window.location.search);
            name = urlParams.get("name");
        }
        const artist = signal<Artist | null>(null);
        const loading = signal(false);
        loading.value = true;
        Api.getArtist(name ?? "")
            .then(a => artist.value = a)
            .finally(() => loading.value = false);

        return Generics.pageFrame(
            ifjs(loading, Generics.loading()),
            ifjs(artist, Artists.artist(artist))
        );
    }

    static artistsFromString(artistString: StringOrSignal, bigName: boolean = true) {
        const artists = signal<string[]>([]);
        if (artistString instanceof Signal) {
            artistString.subscribe(a => {
                artists.value = a.split(",").map(a => a.trim());
            });
        } else {
            artists.value = artistString.split(",").map(a => a.trim());
        }

        return signalMap(artists, create("div").classes("flex"), a => {
            if (a === "Various Artists") {
                return create("span")
                    .text("Various Artists")
                    .build();
            }

            return Generics.link("/artist/" + a, a, bigName ? ["artist-name"] : []);
        });
    }

    static artist(artist: Signal<Artist | null>) {
        const name = compute(a => a?.name ?? "Artist", artist);
        const description = compute(a => a?.description ?? "", artist);
        const id = compute(a => a?.id ?? 0, artist);
        const tracks = compute(a => a?.tracks ?? [], artist);
        const albums = compute(a => a?.albums ?? [], artist);
        const src = compute(i => getImageUrl(MediaFileType.artistLogo, i, RequestableImageSize.s100), id);
        const has_logo = compute(a => a?.has_logo ?? false, artist);

        return create("div")
            .classes("flex-v")
            .children(
                create("div")
                    .classes("flex")
                    .children(
                        ifjs(has_logo, Generics.image(src, ["artist-logo"])),
                        create("div")
                            .classes("flex-v")
                            .children(
                                Generics.heading(1, name, ["bold", "small-line"]),
                                ifjs(description, Generics.heading(2, description)),
                                Generics.paragraph(compute(t => `Released ${t.length} tracks`, tracks)),
                            ).build(),
                    ).build(),
                Generics.heading(2, "Releases"),
                Albums.albumsList(albums),
            ).build();
    }
}